import React, { type Ref } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { LoaderCircle } from 'lucide-react';

import { type ComponentWithTooltipProps, Tooltip } from '@/ui/Tooltip';
import { cn } from '@/utils/styles';
import type { IconComponentProps, IconComponentType } from '@/utils/types';

const buttonVariants = cva(
  'focus-visible:ring-ring cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm transition-colors focus-visible:outline-hidden focus-visible:ring-1 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/80',
        destructive: 'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90',
        outline: 'border border-white/10  bg-tlai-4/60 text-white/80 shadow-xs hover:bg-white/10',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:text-accent-foreground hover:bg-primary/10',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        xs: 'h-6 px-2 text-xs',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-10 px-8',
        icon: 'size-6',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> &
  ComponentWithTooltipProps & {
    asChild?: boolean;
    loading?: boolean;
    Icon?: IconComponentType;
    iconProps?: IconComponentProps;
  };

export const Button = ({
  ref,
  className,
  variant,
  size,
  asChild = false,
  tooltipContent,
  tooltipProps,
  Icon,
  iconProps,
  children,
  loading,
  ...props
}: ButtonProps & {
  ref?: Ref<HTMLButtonElement>;
}) => {
  const Component = asChild ? Slot : 'button';

  const internalTooltipContent = tooltipContent ?? (size === 'icon' ? children : tooltipContent);

  const IconComponent = loading && size === 'icon' ? LoaderCircle : Icon;

  const isDisabled = loading || props.disabled;

  const buttonNode = (
    <Component
      role="button"
      className={cn(buttonVariants({ variant, size }), 'select-none', loading && 'cursor-not-allowed', className)}
      ref={ref}
      {...props}
      disabled={isDisabled}
    >
      {asChild ? (
        children
      ) : (
        <>
          {IconComponent && (
            <IconComponent
              {...iconProps}
              className={cn('size-4', loading && size === 'icon' && 'animate-spin', iconProps?.className)}
            />
          )}
          {loading && size !== 'icon' ? (
            <LoaderCircle className={cn('size-6 animate-spin opacity-75', iconProps?.className)} />
          ) : (
            <>{size !== 'icon' && children}</>
          )}
        </>
      )}
    </Component>
  );

  if (!internalTooltipContent) {
    return buttonNode;
  }

  const tooltipTrigger = isDisabled ? <span className="inline-flex cursor-not-allowed">{buttonNode}</span> : buttonNode;

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>{tooltipTrigger}</Tooltip.Trigger>
      <Tooltip.Content {...tooltipProps}>{internalTooltipContent}</Tooltip.Content>
    </Tooltip>
  );
};

Button.displayName = 'Button';
